import { memo, useMemo } from 'react';

import { useFilters } from 'hooks/useFilters';

import {
  useGetMetricsQuery,
  useGetRegionsQuery,
  useGetTopGamesQuery,
} from 'store/api/configService/analyticsController';

import { appendTopGameColors, getPeriodValuesFromStr } from 'utils/analytics';

import Loader from 'components/Loader/Loader';
import Button from 'components/UI/Button/Button';
import ToggleFilters from 'components/UI/ToggleFilters/ToggleFilters';
import Typography from 'components/UI/Typography/Typography';
import Filters from 'components/pages/Analytics/Filters/Filters';
import GameBars from 'components/pages/Analytics/GameBars/GameBars';
import Section from 'components/pages/Analytics/Layout/Section/Section';
import SectionContent from 'components/pages/Analytics/Layout/SectionContent/SectionContent';
import SectionHeader from 'components/pages/Analytics/Layout/SectionHeader/SectionHeader';

import { LIMITS, OPTIONS, TOP_GAME_COLORS } from 'components/config/analytics';

import { ReactComponent as DownloadIcon } from 'images/Icons/Download.svg';

import styles from './GamesByCategory.module.scss';

const DEFAULT_REGION = 'All world';

const GamesByCategory = () => {
  const {
    filters: { type, ...filters },
    handleChange,
  } = useFilters({
    type: OPTIONS.gameTypes[0].value,
    period: OPTIONS.periodsByCategory[0].value,
    metric: 'BETS',
    region: DEFAULT_REGION,
    country: [],
  });

  const { data, isLoading } = useGetTopGamesQuery({
    category: type,
    period: getPeriodValuesFromStr(filters.period),
    sortBy: filters.metric,
    limit: LIMITS.byCategory,
    ...(filters.region !== DEFAULT_REGION && { region: filters.region }),
    ...(filters.country.length && { country: filters.country.join(',') }),
  });

  const { data: metrics, isLoading: metricsLoading } = useGetMetricsQuery();
  const { data: regions, isLoading: regionsLoading } = useGetRegionsQuery();

  const filtersOptions = useMemo(
    () => [
      {
        title: 'Choose period',
        label: 'period',
        value: filters.period,
        items: OPTIONS.periodsByCategory,
        handleSelect: handleChange,
      },
      {
        title: 'Choose metric',
        label: 'metric',
        value: filters.metric,
        items: metrics,
        handleSelect: handleChange,
        loading: metricsLoading,
      },
      {
        title: 'Region',
        label: 'region',
        value: filters.region,
        items: [
          DEFAULT_REGION,
          ...(regions?.map(({ region }) => region) ?? []),
        ],
        handleSelect: (name, value) => {
          handleChange(name, value);
          handleChange('country', []);
        },
        loading: regionsLoading,
      },
      {
        title: 'Country',
        label: 'country',
        value: filters.country,
        items: regions
          ?.find(({ region }) => filters.region === region)
          ?.countries?.map((country) => country),
        handleSelect: handleChange,
        loading: regionsLoading,
        disabled: filters.region === DEFAULT_REGION,
        multiple: true,
        countTags: 2,
      },
    ],
    [filters, handleChange, metrics, metricsLoading, regions, regionsLoading],
  );

  const games = useMemo(
    () => appendTopGameColors(data, TOP_GAME_COLORS.byCategory),
    [data],
  );

  return (
    <Section className={styles.section}>
      <SectionHeader
        headings={
          <>
            <Typography type="heading-md" weight="medium" uppercase>
              {LIMITS.byCategory} Trending games
            </Typography>
            <Typography type="text-lg" color="grey">
              Select a category, time period, metric, region or country to see
              how the games are performing
            </Typography>
          </>
        }
        filters={
          <>
            <ToggleFilters
              name="type"
              value={type}
              options={OPTIONS.gameTypes}
              onChange={handleChange}
            />
            <Filters options={filtersOptions} />
            <Button
              variant="icon"
              startIcon={<DownloadIcon width={24} height={24} />}
            >
              XLS
            </Button>
          </>
        }
      />
      {!isLoading ? (
        <SectionContent type="bars">
          <GameBars games={games} />
          <Typography type="text-sm" color="grey" align="right">
            01 Jan 2024 - 31 Mar 2024 data is shown
          </Typography>
        </SectionContent>
      ) : (
        <Loader />
      )}
    </Section>
  );
};

export default memo(GamesByCategory);
